//
// Styling wrapper around react-tabs library
//
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { Tabs as Main, Tab, TabList, TabPanel } from 'react-tabs';

import customIcons from '../../styles/icons';

import { colors, fonts, leading } from '../../styles/theme';

const tabStyles = css`
  -webkit-tap-highlight-color: transparent;
  text-align: left;
  textarea {
    display: block;
    padding: 0;
    border: 0;
    outline: 0;
    font-size: ${fonts.sans.size.normal};
    line-height: ${leading.full};
    resize: vertical;
    &:focus,
    &:hover {
      border: 0;
      outline: 0;
    }
  }

  .react-tabs__tab-list {
    margin: 10px 0 0 15px;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
  }

  .react-tabs__tab {
    display: inline-flex;
    background: transparent;
    border-bottom: 4px solid transparent;
    color: ${colors.grey.primaryLight};
    font-size: 13px;
    font-weight: ${fonts.sans.weight.bold};
    line-height: 16px;
    margin: 0 5px -1px 0;
    outline: 0;
    padding: 5px 12px 3px;
    text-transform: uppercase;
    &:hover {
      color: ${colors.blue.primary};
      cursor: pointer;
    }
  }

  .react-tabs__tab-guide {
    align-items: center;
    align-self: center;
    background: ${colors.grey.secondary};
    border-bottom: 4px solid transparent;
    border-radius: 5px;
    border: 0;
    color: ${colors.grey.primaryLight};
    display: inline-flex;
    font-size: 11px;
    font-weight: ${fonts.sans.weight.bold};
    height: 20px;
    letter-spacing: 0.03em;
    line-height: 16px;
    margin: 0 0 -1px auto;
    outline: 0;
    padding: 2px 8px;
    text-transform: uppercase;
    &:before {
      ${fonts.icons}
      content: '${customIcons['info-circle']}';
      display: inline-block;
      font-size: ${fonts.sans.size.small};
      margin-right: 3px;
    }
    &:hover {
      cursor: pointer;
      color: ${colors.black};
      background: ${colors.grey.secondaryDark};
    }
    @media screen and (max-width: 480px) {
      margin-top: 5px;
      margin-bottom: 5px;
    }
    &.react-tabs__tab--selected {
      background-color: ${colors.blue.primaryDark};
      color: ${colors.white};
      &:hover {
        color: ${colors.white};
      }
    }
  }

  .react-tabs__tab--selected {
    color: ${colors.black};
    border-color: ${colors.blue.primaryDark};
    &:hover {
      color: ${colors.black};
      cursor: default;
    }
  }

  .react-tabs__tab--disabled {
    color: ${colors.grey.secondaryDark};
    cursor: default;
    &:hover,
    &:focus {
      color: ${colors.grey.secondaryDark};
      cursor: not-allowed;
    }
  }

  .react-tabs__tab:focus {
    color: ${colors.blue.primary};
  }

  .react-tabs__tab:focus:after {
    content: '';
    position: absolute;
    height: 5px;
    left: -4px;
    right: -4px;
    bottom: -5px;
    background: #fff;
  }

  .react-tabs__tab-panel {
    display: none;
  }

  .react-tabs__tab-panel-preview {
    &.react-tabs__tab-panel--selected {
    }
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: ${colors.black};
      font-weight: ${fonts.sans.weight.bold};
      text-transform: none;
    }
    h1 {
      margin: 0 0 ${leading.half};
    }
    h2,
    h3 {
      margin: 0 0 ${leading.fourth};
    }
    h4,
    h5,
    h6 {
      margin: 0 0 ${leading.eighth};
    }
    strong,
    b {
      font-weight: ${fonts.sans.weight.bold};
    }
    blockquote {
      margin: 11px 22px;
      font-style: italic;
    }
  }

  .react-tabs__tab-panel-guide {
    &.react-tabs__tab-panel--selected {
      background: ${colors.grey.secondary};
      border-color: transparent;
    }
  }

  .react-tabs__tab-panel--selected {
    display: block;
    border: 1px solid ${colors.grey.secondaryDark};
    box-shadow: 0 0 2px 0 ${colors.grey.altLight};
    padding: 15px;
    border-radius: 5px;
    background: ${colors.white};
    font-size: ${fonts.sans.size.normal};
    color: ${colors.black};
  }
`;

const TabsContainer = styled(Main)(tabStyles);

export const Tabs = {
  TabsContainer,
  Tab,
  TabList,
  TabPanel,
};
